/* cwclientelements
   copyright 2019 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import * as React from 'react';
import {providers} from '@logilab/cwclientlibjs';

/**
 * The parameters for the component used to activate/deactivate a relation to another entity
 */
export interface EntityRelationsSelectorProps {
    /**
     * The related entity
     */
    entity: providers.Entity;
    /**
     * Whether the entity is related to the reference one
     */
    isRelated: boolean;
    /**
     * The CSS class name(s) for the component
     */
    className: string;
    /**
     * Handles the event for adding/removing the entity from the relation
     * @param entity The related entity
     * @param isRelated Whether the entity is now related to the reference one
     */
    onToggle: (entity: providers.Entity, isRelated: boolean) => void;
}

/**
 * The parameters for the component used to activate/deactivate a relation to another entity
 */
export interface DefaultEntityRelationsSelectorProps
    extends EntityRelationsSelectorProps {
    /**
     * The size of the widget
     */
    size: number;
    /**
     * The primary color to use for the widget
     */
    colorRelated: string;
    /**
     * The default color to use for the widget
     */
    colorUnrelated: string;
}

/**
 * The default props for the component used to activate/deactivate a relation to another entity
 */
export const DEFAULT_ENTITY_RELATIONS_SELECTOR_DEFAULT_PROPS = {
    className: '',
    size: 18,
    colorUnrelated: '#ccc',
    colorRelated: '#2196F3',
};

/**
 * The default component used to activate/deactivate a relation to another entity
 */
export class DefaultEntityRelationsSelector extends React.Component<
    DefaultEntityRelationsSelectorProps,
    {}
> {
    public static defaultProps =
        DEFAULT_ENTITY_RELATIONS_SELECTOR_DEFAULT_PROPS;

    constructor(props: Readonly<DefaultEntityRelationsSelectorProps>) {
        super(props);
        this.state = {
            isRelated: props.isRelated,
        };
        this.onClickToggle = this.onClickToggle.bind(this);
    }

    componentDidUpdate(
        prevProps: DefaultEntityRelationsSelectorProps,
        prevState: {},
        snapshot: any
    ) {
        if (
            prevProps.isRelated !== this.props.isRelated ||
            prevProps.size !== this.props.size ||
            prevProps.colorUnrelated !== this.props.colorUnrelated ||
            prevProps.colorRelated !== this.props.colorRelated ||
            prevProps.className !== this.props.className ||
            prevProps.entity !== this.props.entity
        ) {
            this.setState({});
        }
    }

    onClickToggle() {
        this.props.onToggle(this.props.entity, !this.props.isRelated);
    }

    render() {
        return (
            <label
                className={this.props.className}
                style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: `${(this.props.size + 4) * 2}px`,
                    height: `${this.props.size + 4 * 2}px`,
                }}
                onClick={this.onClickToggle}
            >
                <span
                    className="slider"
                    style={{
                        position: 'absolute',
                        cursor: 'pointer',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: this.props.isRelated
                            ? this.props.colorRelated
                            : this.props.colorUnrelated,
                        WebkitTransition: '.4s',
                        transition: '.4s',
                    }}
                    onClick={this.onClickToggle}
                />
                <div
                    style={{
                        position: 'absolute',
                        cursor: 'pointer',
                        content: '',
                        height: `${this.props.size}px`,
                        width: `${this.props.size}px`,
                        left: '4px',
                        bottom: '4px',
                        backgroundColor: 'white',
                        WebkitTransition: '.4s',
                        transition: '.4s',
                        WebkitTransform: this.props.isRelated
                            ? `translateX(${this.props.size}px)`
                            : '',
                        msTransform: this.props.isRelated
                            ? `translateX(${this.props.size}px)`
                            : '',
                        transform: this.props.isRelated
                            ? `translateX(${this.props.size}px)`
                            : '',
                    }}
                    onClick={this.onClickToggle}
                />
            </label>
        );
    }
}
